import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Visibility as ViewIcon, AssignmentReturn as ReturnIcon, Print as PrintIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetRentalsQuery } from '../../../store/api/rentalApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

const RentalList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading } = useGetRentalsQuery({ page, limit: 20 });
  const { data: company } = useGetCurrentCompanyQuery();

  const currency = company?.currency || 'USD';
  const currencyDecimals = company?.currencyDecimals ?? 2;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(undefined, { 
      style: 'currency', 
      currency, 
      minimumFractionDigits: currencyDecimals, 
      maximumFractionDigits: currencyDecimals
    }).format(Number(amount) || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Overdue': return 'error';
      case 'Returned': return 'info';
      case 'Draft': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Rental Contracts</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/rentals/new')}
        >
          New Rental
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Contract #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Total Amount</TableCell>
              <TableCell align="right">Deposit Amount</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Loading...</TableCell>
              </TableRow>
            ) : data?.contracts?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">No rental contracts found.</TableCell>
              </TableRow>
            ) : (
              data?.contracts?.map((contract) => (
                <TableRow key={contract.id}>
                  <TableCell>{contract.contractNumber}</TableCell>
                  <TableCell>{contract.Customer?.name || 'Unknown'}</TableCell>
                  <TableCell>{new Date(contract.startDate).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(contract.endDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip label={contract.status} color={getStatusColor(contract.status)} size="small" />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(contract.grandTotal)}</TableCell>
                  <TableCell align="right">{formatCurrency(contract.depositAmount)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => navigate(`/rentals/view/${contract.id}`)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print Bill">
                      <IconButton size="small" color="secondary" onClick={() => navigate(`/rentals/${contract.id}/print`)}>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {contract.status === 'Active' || contract.status === 'Overdue' ? (
                      <Tooltip title="Process Return">
                        <IconButton size="small" color="primary" onClick={() => navigate(`/rentals/${contract.id}/return`)}>
                          <ReturnIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RentalList;
